import { useCallback, useMemo } from 'react';
import { useGetCampaignOverlappingSummary, useDownloadUniqueCampaignZipCSV, OverlappingCard } from 'entities/campaign';
import type { ManagerCampaignModel } from 'entities/campaign';
import { Button } from 'shared/ui/Button';
import { Divider } from 'shared/ui/Divider';
import { logError } from 'shared/lib/log-errors';
import type { DataAttributes } from 'shared/lib/data-auto-test';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import type { InfoTooltip } from 'shared/ui/CommonCard';
import { useContractorDateWithTimeZone } from 'shared/lib/timezones';

import styles from './OverlappingField.module.scss';

const campaignTypeHeader = {
  lead: 'Leads generated from unique ZIPs:',
  appt: 'Appointments generated from unique ZIPs:',
  sales: 'Sales generated from unique ZIPs:',
};

interface LeadItem {
  period: string;
  value: number;
  valueAttributes?: DataAttributes['attributes'];
}
function LeadItem({ period, value, valueAttributes }: LeadItem) {
  return (
    <p className={styles.leads_item}>
      <span className="pr-2">{period}:</span>
      <span
        className="pl-2 font-medium text-slate-900"
        {...valueAttributes}
      >
        {value}
      </span>
    </p>
  );
}

interface OverlappingField {
  campaignId: number;
  campaignType: ManagerCampaignModel['type'];
  appliesAt: string | null;
  onViewClick: () => void;
}

export function OverlappingField({ campaignId, onViewClick, campaignType, appliesAt }: OverlappingField) {
  const { download } = useDownloadUniqueCampaignZipCSV(campaignId);
  const overlappingSummaryQuery = useGetCampaignOverlappingSummary(campaignId);
  const getPendingDate = useContractorDateWithTimeZone();
  const handleDownload = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      download().catch(logError);
    },
    [download]
  );
  const overlappingData = overlappingSummaryQuery.data?.data;

  const overlappingDataCard = useMemo(() => {
    if (overlappingData) {
      return {
        current: overlappingData.zipCodes.unique.thisWeek,
        previous: overlappingData.zipCodes.inUse.thisWeek,
        pendingChanges: overlappingData.zipCodes.unique.pendingChanges,
        percentage: Math.round(
          (100 * overlappingData.zipCodes.unique.thisWeek) / overlappingData.zipCodes.inUse.thisWeek
        ),
        difference: overlappingData.zipCodes.unique.thisWeek - overlappingData.zipCodes.unique.lastWeek,
      };
    }

    return null;
  }, [overlappingData]);

  const hasOverlapping = overlappingDataCard && (overlappingDataCard.current > 0 || overlappingDataCard.previous > 0);
  const cardColor = overlappingDataCard?.pendingChanges ? 'border-orange-200' : 'border-deep-blue-100';

  const overlappingIcons: InfoTooltip[] = [
    {
      content: `All unique ZIP Codes within a campaign, excluding those filtered by 'minimum income' and those that overlap with other campaigns offering the same service.`,
    },
  ];

  if (overlappingDataCard?.pendingChanges && appliesAt) {
    overlappingIcons.push({
      iconName: 'update',
      colorClassName: 'text-orange-500',
      content: `Unique ZIPs changes will be applied on ${getPendingDate(appliesAt)}`,
      attributes: {
        icon: getDataAutoTestAttributes(['overlapping-update-icon']).attributes,
        tooltip: getDataAutoTestAttributes(['overlapping-update-tooltip']).attributes,
      },
    });
  }

  return (
    <div>
      <Divider />
      <div className="flex justify-between">
        <p className="text-lg font-semibold text-slate-900">Overlapping</p>
        {hasOverlapping && (
          <Button
            type="button"
            className="w-24"
            variant="outline"
            size="medium"
            onClick={onViewClick}
            {...getDataAutoTestAttributes(['button-view-overlapping'])}
          >
            View
          </Button>
        )}
      </div>
      <OverlappingCard
        className="mt-4"
        size="large"
        label="Unique ZIPs"
        color={cardColor}
        infoTooltips={overlappingIcons}
        actionIconName="file_download"
        isActionDisabled={!hasOverlapping}
        onAction={handleDownload}
        cardData={overlappingDataCard}
      />
      {!!overlappingData && (
        <div className="mt-4">
          <span className="mb-2 block text-sm font-medium text-slate-900">{campaignTypeHeader[campaignType]}</span>
          <LeadItem
            period="Yesterday"
            value={overlappingData.generatedItems.unique.yesterday}
            valueAttributes={getDataAutoTestAttributes(['overlapping-yesterday']).attributes}
          />
          <LeadItem
            period="This week"
            value={overlappingData.generatedItems.unique.thisWeek}
            valueAttributes={getDataAutoTestAttributes(['overlapping-this-week']).attributes}
          />
          <LeadItem
            period="Last week"
            value={overlappingData.generatedItems.unique.lastWeek}
            valueAttributes={getDataAutoTestAttributes(['overlapping-last-week']).attributes}
          />
          <LeadItem
            period="Two weeks ago"
            value={overlappingData.generatedItems.unique.weekBeforeLast}
            valueAttributes={getDataAutoTestAttributes(['overlapping-week-before-last']).attributes}
          />
        </div>
      )}
    </div>
  );
}
